import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { approveLiquidationRequest } from "../../redux/slice/liquidationSlice";
import {Popconfirm, Spin} from "antd";
import {message} from "../../utils/antdFeedback";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Landmark } from "lucide-react";
import {adminRequest, getReturnedObject} from "../../api/adminApi";
import { HeroHeader, Card, COLORS } from "../../designSystem";

function ApproveLiquidation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const { token, id: userId } = useSelector((state: RootState) => state.account);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!token || !id) return;

    adminRequest(`/group-savings/liquidation-request/${id}`, {token})
      .then((json) => setData(getReturnedObject(json)))
      .catch((err) => {
        console.error("Error loading data:", err);
        message.error("Failed to load request details.");
      })
      .finally(() => setLoading(false));
  }, [token, id]);

 const handleApprove = () => {
  if (!data || !userId) return;

  setApproving(true);

  dispatch(
    approveLiquidationRequest({
      token,
      userId,
      entityId: data.entityId,
      requestId: data.id
    })
  )
    .unwrap()
    .then(() => {
      message.success("Liquidation approved successfully.");
      navigate("/requests");
    })
    .catch((err) => {
      message.error(err || "Approval failed.");
    })
    .finally(() => setApproving(false));
};

  if (loading) {
    return (
      <AdminLayout>
        <div className="m-10">
          <Spin tip="Loading request details..." />
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="m-10">
          <p>Unable to load liquidation request.</p>
        </div>
      </AdminLayout>
    );
  }

  const needsApproval = data.status === 3;

  return (
  <AdminLayout>
    <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{ background: COLORS.surface }}>
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center font-medium bg-transparent border-none cursor-pointer transition-all"
          style={{ color: COLORS.brand }}
        >
          <ArrowLeftOutlined className="mr-2" /> Back
        </button>
        <HeroHeader
          icon={Landmark}
          title="Approve Liquidation Request"
          subtitle="Verify the request and approve the payout."
        />

        <Card className="mt-8 p-8 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm" style={{ color: COLORS.text }}>
            <div>
              <span className="font-semibold block">Requested By:</span>
              <span>{data.requestedBy?.fullName}</span>
            </div>
            <div>
              <span className="font-semibold block">Email:</span>
              <span>{data.requestedBy?.email}</span>
            </div>
            <div>
              <span className="font-semibold block">Amount:</span>
              <span className="font-mono">{`₦${parseFloat(data.amount).toLocaleString()}`}</span>
            </div>
            <div>
              <span className="font-semibold block">Group:</span>
              <span>{data.group?.name}</span>
            </div>
            <div>
              <span className="font-semibold block">Group Balance:</span>
              <span className="font-mono">{`₦${parseFloat(data.group?.activeBalance || 0).toLocaleString()}`}</span>
            </div>
            <div>
              <span className="font-semibold block">Request Date:</span>
              <span>{new Date(data.dateCreated).toLocaleString()}</span>
            </div>
            <div>
              <span className="font-semibold block">Status:</span>
              <span>{getStatusLabel(data.status)}</span>
            </div>
          </div>

          <div className="pt-4">
            {needsApproval ? (
              <Popconfirm
                title="Are you sure you want to approve this request?"
                onConfirm={handleApprove}
                okText="Yes, Approve"
                cancelText="Cancel"
                disabled={approving}
              >
                <button
                  disabled={approving}
                  className="h-12 px-6 rounded-lg text-white font-semibold transition-all border-none outline-none disabled:opacity-60"
                  style={{ background: approving ? COLORS.brandHover : COLORS.brand, cursor: approving ? "not-allowed" : "pointer" }}
                >
                  {approving ? "Approving..." : "Approve Request"}
                </button>
              </Popconfirm>
            ) : (
              <button
                disabled
                className="h-12 px-6 rounded-lg text-white font-semibold cursor-not-allowed border-none outline-none"
                style={{ background: "#9ca3af" }}
              >
                No Approval Required
              </button>
            )}
          </div>
        </Card>
      </div>
    </main>
  </AdminLayout>
);

}

function getStatusLabel(status: number): string {
  switch (status) {
    case 1:
      return "Processing";
    case 2:
      return "Completed";
    case 3:
      return "Pending Approval";
    case 5:
      return "Payout Pending";
    default:
      return "Unknown";
  }
}

export default ApproveLiquidation;
